import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { Link, useSearch } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { useSession } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

// A failed social/SSO sign-in redirects here with `?error=<code>` (and sometimes `&error_description`).
// better-auth funnels every OAuth/SSO failure through one error redirect, so the codes we see come
// from three places:
//   1. better-auth's own callback checks (e.g. state_not_found, invalid_code, email_not_found)
//   2. its account-linking / sign-up seam (its internal error strings with spaces → underscores,
//      e.g. "signup disabled" → signup_disabled)
//   3. the IdP itself — raw OAuth2 / OIDC error codes forwarded verbatim (access_denied, …)
// Plus our own WORK_EMAIL_REQUIRED, thrown from the auth-service user-create hook.
// Enterprise OIDC providers (via the SSO plugin) reuse the same callback, so these codes cover them too.
//
// `kind` drives behaviour beyond the message:
//   - "org"       → the user's IdP/org policy blocked them; show the IdP's description (helps them
//                    tell their admin why), but it's not our bug so we don't report it.
//   - "transient" → retryable (state/CSRF/timeout, IdP hiccup); show the description if present.
//   - "config"    → our SSO/app-registration is misconfigured; report to Sentry so we get alerted.
//   - "orgConfig" → the org misconfigured their IdP; report to Sentry so we can help them fix it.
//   - "server"    → a server-side failure creating the user/session; report to Sentry.
//   - "user"      → the user can resolve it themselves; plain copy, nothing logged.
type SocialSignInErrorKind = "user" | "org" | "transient" | "config" | "orgConfig" | "server";

type SocialSignInErrorInfo = {
  message: string;
  kind: SocialSignInErrorKind;
};

// Grouped copy — many distinct codes collapse to the same remediation.
const NO_VERIFIED_EMAIL =
  "We couldn't get a verified email address from that provider. Try signing in another way.";
const LINKING_CONFLICT =
  "We couldn't link this account to your existing Stotles account. Sign in the way you did originally, or contact support.";
// `account_not_linked` is narrower than the rest of the linking-conflict group, so it gets its own
// copy. better-auth only emits it once it has found a user for the address the provider returned and
// then refused to attach the provider to them (there's no matching `auth.account` row, and its
// linking gate wasn't satisfied). So the account definitely exists and we can say so — we just can't
// name the address, because the error redirect carries only the code.
//
// Not to be confused with `account_already_linked_to_different_user`, which is the opposite case:
// the provider identity IS attached, to someone else.
const ACCOUNT_EXISTS_NOT_LINKED =
  "There's already a Stotles account for this email address, but we couldn't verify the email from this provider to link the two. Sign in the way you did originally — with your password, or another provider.";
const ACCESS_DENIED =
  "Your organisation's identity provider blocked this sign-in. You may not be assigned to Stotles, or an administrator needs to approve access — please contact your IT administrator.";
const TRANSIENT = "Your sign-in session expired or was interrupted. Please try again.";
const CONFIG_ERROR = "Single sign-on isn't configured correctly. Please contact Stotles support.";
const CONFIG_ORG_ERROR =
  "Your organisation's custom SSO configuration is invalid, please contact your IT administrator.";
// An app-launcher tile (Google Workspace, Okta) posts an assertion we didn't ask for and refuse.
// auth-service retries those as a normal sign-in, so reaching this page means the retry failed —
// the organisation's SAML setup is fine, and blaming it sends their IT team on a wild goose chase.
const IDP_INITIATED_ERROR =
  "Signing in from your organisation's app launcher isn't supported. Use the log in link below and enter your work email — you'll be signed in through your organisation as usual.";
const DEFAULT_SSO_SIGN_IN_ERROR = "We couldn't sign you in. Please try again.";
// A malformed connect request — wrong client id, stale grant, unregistered resource. Nothing the
// user can fix, and nothing wrong with their account.
const AUTHORIZATION_REQUEST_ERROR =
  "The application's request to connect wasn't valid, so we couldn't authorise it. Nothing on your account has changed. If you're setting this up yourself, check the application's configuration; otherwise contact Stotles support.";

const DEFAULT_SSO_SIGN_IN_ERROR_INFO: SocialSignInErrorInfo = {
  message: DEFAULT_SSO_SIGN_IN_ERROR,
  kind: "server",
};

const SSO_SIGN_IN_ERRORS: Record<string, SocialSignInErrorInfo> = {
  // Our work-email block (auth-service user-create hook).
  work_email_required: {
    message: "Please sign in with your work email — personal email accounts aren't supported.",
    kind: "user",
  },
  // No usable / verified email from the provider.
  email_not_found: { message: NO_VERIFIED_EMAIL, kind: "user" },
  email_not_verified: { message: NO_VERIFIED_EMAIL, kind: "user" },
  unable_to_get_user_info: { message: NO_VERIFIED_EMAIL, kind: "user" },
  // Account-linking conflicts.
  account_already_linked_to_different_user: { message: LINKING_CONFLICT, kind: "user" },
  account_not_linked: { message: ACCOUNT_EXISTS_NOT_LINKED, kind: "user" },
  email_does_not_match: { message: LINKING_CONFLICT, kind: "user" },
  unable_to_link_account: { message: LINKING_CONFLICT, kind: "user" },
  // No account yet / signups disabled.
  signup_disabled: {
    message: "Signups are disabled and there's no Stotles account for this login yet.",
    kind: "user",
  },
  // IdP / org policy blocked the user (standard OAuth2 / OIDC prompt codes) — enterprise-critical.
  access_denied: { message: ACCESS_DENIED, kind: "org" },
  consent_required: { message: ACCESS_DENIED, kind: "org" },
  interaction_required: { message: ACCESS_DENIED, kind: "org" },
  login_required: { message: ACCESS_DENIED, kind: "org" },
  account_selection_required: { message: ACCESS_DENIED, kind: "org" },
  // Transient — state/CSRF/timeout/back-button, or an IdP-side hiccup.
  no_code: { message: TRANSIENT, kind: "transient" },
  invalid_code: { message: TRANSIENT, kind: "transient" },
  invalid_callback_request: { message: TRANSIENT, kind: "transient" },
  state_not_found: { message: TRANSIENT, kind: "transient" },
  nonce_binding_missing: { message: TRANSIENT, kind: "transient" },
  temporarily_unavailable: { message: TRANSIENT, kind: "transient" },
  server_error: { message: TRANSIENT, kind: "transient" },
  // Misconfiguration — our SSO / app registration, not the user's problem. Reported to Sentry.
  oauth_provider_not_found: { message: CONFIG_ERROR, kind: "config" },
  issuer_missing: { message: CONFIG_ERROR, kind: "config" },
  issuer_mismatch: { message: CONFIG_ERROR, kind: "config" },
  no_callback_url: { message: CONFIG_ERROR, kind: "config" },
  invalid_scope: { message: CONFIG_ERROR, kind: "config" },
  unauthorized_client: { message: CONFIG_ERROR, kind: "config" },
  invalid_request: { message: CONFIG_ERROR, kind: "config" },
  unsupported_response_type: { message: CONFIG_ERROR, kind: "config" },
  // Server-side failures creating the user / session.
  unable_to_create_user: { message: DEFAULT_SSO_SIGN_IN_ERROR, kind: "server" },
  unable_to_create_session: { message: DEFAULT_SSO_SIGN_IN_ERROR, kind: "server" },
  // The company has switched this sign-in method off, or requires enterprise SSO.
  login_method_not_allowed: {
    message: "Your company doesn't allow signing in this way. Please use another sign-in method.",
    kind: "org",
  },
  sso_required: {
    message: "Your company uses single sign-on (SSO). Please log in using your company's SSO.",
    kind: "org",
  },
  // Enterprise SSO
  sso_domain_not_allowed: { message: CONFIG_ORG_ERROR, kind: "orgConfig" },
  sso_domain_not_verified: { message: CONFIG_ORG_ERROR, kind: "orgConfig" },
  // OIDC
  discovery_failed: { message: CONFIG_ORG_ERROR, kind: "orgConfig" },
  invalid_provider: { message: CONFIG_ORG_ERROR, kind: "orgConfig" },
  // SAML. `unsolicited_response` is "server" so we get a Sentry report: the retry failing is ours
  // to look at, not the organisation's.
  unsolicited_response: { message: IDP_INITIATED_ERROR, kind: "server" },
  invalid_saml_response: { message: CONFIG_ORG_ERROR, kind: "orgConfig" },
  replay_detected: { message: TRANSIENT, kind: "transient" },
};

/**
 * Failures from *our own* OAuth provider — a downstream app being authorised (e.g. an MCP client),
 * not a sign-in. They land here because the auth-service points `onAPIError.errorURL` at this page.
 *
 * Only codes that a social/SSO sign-in can't also emit. Shared ones like `invalid_request` keep their
 * sign-in copy, since the code alone doesn't say which flow produced it.
 */
const REDIRECT_URI_MISMATCH =
  "The redirect URL the application sent doesn't match the one registered with Stotles. Check that the redirect URL configured in your application matches exactly what was registered, or contact Stotles support.";

const AUTHORIZATION_ERRORS: Record<string, SocialSignInErrorInfo> = {
  invalid_client: { message: AUTHORIZATION_REQUEST_ERROR, kind: "config" },
  invalid_grant: { message: AUTHORIZATION_REQUEST_ERROR, kind: "config" },
  invalid_redirect: { message: REDIRECT_URI_MISMATCH, kind: "config" },
  invalid_target: { message: AUTHORIZATION_REQUEST_ERROR, kind: "config" },
  unsupported_grant_type: { message: AUTHORIZATION_REQUEST_ERROR, kind: "config" },
};

/** Whether this code came from authorising an application rather than from signing in. */
export function isAuthorizationError(error: string | undefined): boolean {
  return error ? error.toLowerCase() in AUTHORIZATION_ERRORS : false;
}

/**
 * Look up the copy + category for a social/SSO sign-in error code. Unknown or missing codes fall
 * back to a generic server-kind error (an unknown code means better-auth or an IdP emitted something
 * we don't map yet — the page reports that to Sentry so we can add it).
 */
export function ssoSignInError(error: string | undefined): SocialSignInErrorInfo {
  if (!error) return DEFAULT_SSO_SIGN_IN_ERROR_INFO;
  const code = error.toLowerCase();
  return SSO_SIGN_IN_ERRORS[code] ?? AUTHORIZATION_ERRORS[code] ?? DEFAULT_SSO_SIGN_IN_ERROR_INFO;
}

// The IdP's own `error_description` is useful context on org-policy and transient failures (e.g.
// "user not assigned to this application"), but noise on the plain user-fixable / config cases.
function shouldShowErrorDescription(kind: SocialSignInErrorKind): boolean {
  return kind === "org" || kind === "orgConfig" || kind === "transient";
}

/**
 * The failures the user can't self-resolve — a misconfigured SSO / app registration, a server-side
 * create failure, or a code we don't map yet. These are the ones worth (a) reporting to Sentry and
 * (b) exposing raw technical details for, so a user can quote them in a support request. Everything
 * else has actionable copy the user can act on themselves.
 */
function isUnresolvableError(error: string | undefined): {
  report: boolean;
  kind: SocialSignInErrorKind;
  isUnmapped: boolean;
} {
  const { kind } = ssoSignInError(error);
  const code = (error ?? "").toLowerCase();
  const isUnmapped =
    SSO_SIGN_IN_ERRORS[code] === undefined && AUTHORIZATION_ERRORS[code] === undefined;
  // Requires an actual code — a resolved-but-empty session (no `error`) has nothing to surface.
  const report =
    Boolean(error) &&
    (kind === "config" || kind === "orgConfig" || kind === "server" || isUnmapped);
  return { report, kind, isUnmapped };
}

/**
 *  Landing page for the SSO redirect (see signInWithProvider). The OAuth flow
 *  leaves the SPA, so there's no in-page sign-in result to inspect — we resolve the freshly-created
 *  session here and forward the user on, mirroring BetterAuthLoginPage's post-sign-in handling.
 * @returns
 */
export function BetterAuthCallbackPage() {
  // Sanitised to an internal path + bounded error code + bounded description by the route's
  // validateSearch (see validateCallbackSearch).
  const { redirect, error, error_description, requested_redirect_uri, client_id } = useSearch({
    strict: false,
  });
  const { data: session, isPending } = useSession();

  const hasSession = Boolean(session?.user);

  // Sentry event id for the reported failure, surfaced in the technical details so a user can quote
  // it in a support request (mirrors ErrorBoundaryFallback's event-id affordance).
  const [eventId, setEventId] = useState<string | undefined>();

  useEffect(() => {
    if (error || isPending) return;

    // A full navigation (not a router push) so the destination boots fresh: the root-route guard
    // then enforces 2FA setup if the company requires it (see rootRoute.beforeLoad) — we no longer
    // special-case it here.
    if (hasSession) {
      window.location.assign(redirect ?? "/");
    }
  }, [error, isPending, hasSession, redirect]);

  // Report the failures that indicate a bug on our side or a code we don't map yet — user-fixable and
  // org-policy failures are expected, so we stay quiet on those to avoid noise. Keep the returned
  // event id so it can be shown in the technical details below.
  useEffect(() => {
    const { report, kind, isUnmapped } = isUnresolvableError(error);
    if (!report) return;

    const id = Sentry.captureMessage(`SSO sign-in failed: ${error}`, {
      level: kind === "config" ? "error" : "warning",
      extra: { error, error_description, kind, unmapped: isUnmapped },
    });
    setEventId(id);
  }, [error, error_description]);

  // A failed social sign-in lands here with an `?error=` code; a resolved-but-empty session is the
  // catch-all failure. Show a message tailored to the code where we have one.
  if (error || (!isPending && !hasSession)) {
    const { message, kind } = ssoSignInError(error);
    // Show raw technical details only for the failures we can't self-resolve, so a user can copy
    // them (and the reference id) into a support request.
    const { report: showTechnicalDetails } = isUnresolvableError(error);
    // An app that failed to be authorised also lands here, and that user was already signed in —
    // "Sign-in failed" would misdirect them.
    const heading = isAuthorizationError(error)
      ? "Couldn't authorise the application"
      : "Sign-in failed";
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <Text h1 color="sysDestructiveDefault">
            {heading}
          </Text>
          <Text color="sysTextSecondary">{message}</Text>
          {error_description && shouldShowErrorDescription(kind) && (
            <Text description color="sysTextSecondary">
              {error_description}
            </Text>
          )}
          <Text color="sysTextSecondary">
            {isAuthorizationError(error) ? (
              <Link to="/">Back to Stotles</Link>
            ) : (
              <Link to="/auth/login">Back to log in</Link>
            )}
          </Text>
          {showTechnicalDetails && (
            <TechnicalDetails>
              <summary>Technical details</summary>
              <Flex column gap="4px">
                {eventId && (
                  <Text description color="sysTextSecondary">
                    Reference ID: <code>{eventId}</code>
                  </Text>
                )}
                {error && (
                  <Text description color="sysTextSecondary">
                    Error: <code>{error}</code>
                  </Text>
                )}
                {error_description && (
                  <Text description color="sysTextSecondary">
                    Details: <code>{error_description}</code>
                  </Text>
                )}
                {client_id && (
                  <Text description color="sysTextSecondary">
                    Client ID: <code>{client_id}</code>
                  </Text>
                )}
                {requested_redirect_uri && (
                  <Text description color="sysTextSecondary">
                    Redirect URL sent: <code>{requested_redirect_uri}</code>
                  </Text>
                )}
              </Flex>
            </TechnicalDetails>
          )}
        </Card>
      </Page>
    );
  }

  // Session resolving or redirect in flight.
  return <CentredSpinner />;
}

const TechnicalDetails = styled.details({
  wordBreak: "break-word",
  textAlign: "left",
});
